import { useMemo } from 'react';
import { ArrowRight, CheckCircle2, Gavel, Layers3, Target, Wallet } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useLocalization } from '@/hooks/useLocalization';
import { usePortfolioProjects } from '@/hooks/usePortfolioProjects';
import { useExecutiveData } from '@/hooks/useExecutiveData';
import { healthTone, summarisePortfolio, worstFirst, type HealthTone } from '@/lib/pmoSummary';
import { decisionQueue, decisionStats, isOverdue, pillarStats } from '@/lib/homeSummaries';
import { HomeActions, HomeEmpty, HomePanel, HomeRow, StatRow, StatTile } from './HomeFrame';

interface Props { onNavigate: (view: string, params?: Record<string, string>) => void; }

const TONE_CLASS: Record<HealthTone, string> = { 'on-track': 'bg-success/10 text-foreground', 'at-risk': 'bg-warning/15 text-foreground', critical: 'bg-destructive/10 text-destructive' };
const TONE_LABEL: Record<HealthTone, string> = { 'on-track': 'On track', 'at-risk': 'At risk', critical: 'Critical' };

/**
 * Executive home: decisions that need the executive, portfolio exceptions, strategy and human-approved summaries.
 * No tasks, inbox or unreviewed AI output. The heading comes from the shell (roleHome).
 */
const ExecutiveBrief = ({ onNavigate }: Props) => {
  const { t, tx, currency, shortDate, ago } = useLocalization();
  const projectsQuery = usePortfolioProjects();
  const x = useExecutiveData();
  const now = useMemo(() => new Date(), []);

  const allProjects = useMemo(() => projectsQuery.data ?? [], [projectsQuery.data]);
  const portfolio = useMemo(() => summarisePortfolio(allProjects), [allProjects]);
  const attention = useMemo(() => worstFirst(allProjects).filter((p) => healthTone(p.health) !== 'on-track').slice(0, 6), [allProjects]);
  const queue = useMemo(() => decisionQueue(x.decisions, now).slice(0, 6), [x.decisions, now]);
  const dStats = useMemo(() => decisionStats(x.decisions, now), [x.decisions, now]);
  const pStats = useMemo(() => (x.pillars ? pillarStats(x.pillars) : null), [x.pillars]);

  if (x.isLoading || projectsQuery.isLoading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-label={t('Loading…')}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}</div>
        <div className="grid gap-6 lg:grid-cols-3"><Skeleton className="h-72 lg:col-span-2" /><Skeleton className="h-72" /></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <HomeActions>
        <Button variant="outline" onClick={() => onNavigate('strategy')}>{t('Open strategy')}</Button>
        <Button onClick={() => onNavigate('decisions')}>{t('Open decision log')}</Button>
      </HomeActions>

      <StatRow>
        <StatTile
          label={t('Decisions waiting')}
          icon={<Gavel className="h-4 w-4" />}
          value={dStats.total}
          tone={dStats.overdue > 0 ? 'critical' : dStats.total > 0 ? 'warning' : 'default'}
          hint={dStats.total === 0 ? t('Nothing is waiting for a decision.')
            : `${t('{0} overdue', { 0: dStats.overdue })}${dStats.amount !== null ? ` · ${t('{0} at stake', { 0: currency(dStats.amount, { compact: true }) })}` : ''}`}
          onClick={() => onNavigate('decisions')}
        />
        <StatTile
          label={t('Portfolio health')}
          icon={<Layers3 className="h-4 w-4" />}
          value={portfolio.total}
          tone={portfolio.critical > 0 ? 'critical' : portfolio.atRisk > 0 ? 'warning' : 'default'}
          hint={portfolio.total ? t('{0} at risk, {1} critical', { 0: portfolio.atRisk, 1: portfolio.critical }) : t('No projects synced yet.')}
          onClick={() => onNavigate('portfolio')}
        />
        <StatTile
          label={t('Budget')}
          icon={<Wallet className="h-4 w-4" />}
          value={portfolio.budgetUsedPct === null ? '—' : `${portfolio.budgetUsedPct}%`}
          tone={portfolio.overBudget > 0 ? 'critical' : 'default'}
          hint={portfolio.budget === null ? t('Not recorded')
            : `${t('{0} of {1} spent', { 0: currency(portfolio.spent ?? 0, { compact: true }), 1: currency(portfolio.budget, { compact: true }) })}${portfolio.overBudget ? ` · ${t('{0} over budget', { 0: portfolio.overBudget })}` : ''}`}
          onClick={() => onNavigate('portfolio')}
        />
        <StatTile
          label={t('Strategy')}
          icon={<Target className="h-4 w-4" />}
          value={!pStats || pStats.avgProgress === null ? '—' : `${pStats.avgProgress}%`}
          hint={!pStats ? t('Not recorded') : pStats.total === 0 ? t('No strategic pillars defined yet.') : t('{0} of {1} pillars on target', { 0: pStats.onTarget, 1: pStats.total })}
          onClick={() => onNavigate('strategy')}
        />
      </StatRow>

      <div className="grid gap-6 lg:grid-cols-3">
        <HomePanel className="lg:col-span-2" title={t('Decisions waiting on you')} hint={t('Overdue first, then the nearest due date.')} action={<Badge variant="outline">{dStats.total}</Badge>}>
          {queue.length === 0 ? (
            <HomeEmpty>{x.decisionsFailed ? t('Decisions could not be loaded. Try again shortly.') : t('Nothing is waiting for a decision.')}</HomeEmpty>
          ) : queue.map((dec) => {
            const late = isOverdue(dec.due_date, now);
            return (
              <HomeRow key={dec.id} onClick={() => onNavigate('decisions')}>
                <Gavel className={cn('h-4 w-4 shrink-0', late ? 'text-destructive' : 'text-primary')} aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{dec.title}</span>
                  <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                    {[dec.project_name, dec.due_date ? (late ? t('overdue since {0}', { 0: shortDate(dec.due_date) }) : t('due {0}', { 0: shortDate(dec.due_date) })) : t('No due date'),
                      typeof dec.amount === 'number' && dec.amount > 0 ? currency(dec.amount, { compact: true }) : null].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {dec.priority && <Badge variant="outline" className="shrink-0">{tx(dec.priority)}</Badge>}
              </HomeRow>
            );
          })}
          {dStats.total > queue.length && (
            <Button variant="ghost" size="sm" className="w-full" onClick={() => onNavigate('decisions')}>{t('View all')} ({dStats.total})</Button>
          )}
        </HomePanel>

        <HomePanel title={t('Projects needing attention')} hint={t('Only projects that are at risk or critical.')} action={<Button variant="ghost" size="sm" onClick={() => onNavigate('portfolio')}>{t('View all')} <ArrowRight className="ms-1 h-4 w-4 rtl:rotate-180" /></Button>}>
          {attention.length === 0 ? (
            <HomeEmpty>{projectsQuery.isError ? t('Projects could not be loaded.') : portfolio.total ? t('Every project is on track.') : t('No projects synced yet.')}</HomeEmpty>
          ) : attention.map((p) => {
            const tone = healthTone(p.health);
            return (
              <HomeRow key={p.id} onClick={() => onNavigate('risks', { project: p.id })} label={t('{0}: {1}. Open its risks.', { 0: p.name, 1: t(TONE_LABEL[tone]) })}>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold">{p.name}</span>
                    <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium', TONE_CLASS[tone])}>{t(TONE_LABEL[tone])}</span>
                  </span>
                  <Progress value={p.progress ?? 0} className="mt-2 h-1.5" />
                </span>
                <span className="font-mono text-xs tabular-nums text-muted-foreground">{p.progress ?? 0}%</span>
              </HomeRow>
            );
          })}
        </HomePanel>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <HomePanel className="lg:col-span-2" title={t('Approved summaries')} hint={t('AI output a person has reviewed and approved. Nothing unreviewed appears here.')}>
          {x.approved.length === 0 ? (
            <HomeEmpty>{x.approvedFailed ? t('Approved summaries could not be loaded.') : t('No approved summaries yet.')}</HomeEmpty>
          ) : x.approved.map((a) => (
            <HomeRow key={a.id} onClick={() => onNavigate('reports')}>
              <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{a.title}</span>
                {a.summary && <span className="mt-0.5 block text-xs text-muted-foreground line-clamp-2">{a.summary}</span>}
                <span className="mt-0.5 block text-[11px] text-muted-foreground">{t('approved {0}', { 0: ago(a.reviewed_at ?? a.created_at) })}</span>
              </span>
            </HomeRow>
          ))}
        </HomePanel>

        <HomePanel title={t('Strategic Pillars')} action={<Button variant="ghost" size="sm" onClick={() => onNavigate('strategy')}>{t('View all')} <ArrowRight className="ms-1 h-4 w-4 rtl:rotate-180" /></Button>}>
          {!x.pillars ? <HomeEmpty>{t('Not recorded')}</HomeEmpty> : x.pillars.length === 0 ? (
            <HomeEmpty>{t('No strategic pillars defined yet.')}</HomeEmpty>
          ) : x.pillars.slice(0, 6).map((pl) => (
            <div key={pl.id} className="rounded-lg border border-border p-3">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate font-medium">{pl.name}</span>
                <span className="font-mono text-xs tabular-nums text-muted-foreground">
                  {typeof pl.progress_pct === 'number' ? `${Math.round(pl.progress_pct)}%` : '—'}{typeof pl.target_pct === 'number' ? ` / ${Math.round(pl.target_pct)}%` : ''}
                </span>
              </div>
              <Progress value={pl.progress_pct ?? 0} className="mt-2 h-1.5" aria-label={t('{name} progress', { name: pl.name })} />
            </div>
          ))}
        </HomePanel>
      </div>
    </div>
  );
};

export default ExecutiveBrief;
